import { useState } from 'react';
import {
  isInWebView,
  getWebViewType,
  sendMessage,
  sendHandoff,
  sendPaymentFailed,
  sendPaymentSuccess,
  sendPaymentCancelled,
  sendFailed,
  sendRestoreFailed,
  sendRestoreSuccess,
  type WebViewMessage,
} from '@/hooks/useWebViewMessaging';

const win = typeof window !== 'undefined' ? window : null;

export default function TestWebViewPage() {
  const [detection] = useState(() =>
    win
      ? {
          isWebView: isInWebView(),
          webViewType: getWebViewType(),
          hasReactNativeWebView: !!(win as Window & { ReactNativeWebView?: unknown }).ReactNativeWebView,
          hasWebkitHandlers: !!(win as Window & { webkit?: { messageHandlers?: unknown } }).webkit?.messageHandlers,
          hasParent: win.parent !== win,
        }
      : { isWebView: false, webViewType: 'none' as const, hasReactNativeWebView: false, hasWebkitHandlers: false, hasParent: false },
  );

  const testMessage: WebViewMessage = {
    type: 'handoff',
    token: 'test-token-123',
  };

  const sendTestMessage = () => {
    sendMessage(testMessage);
  };

  const sendHandoffMessage = () => {
    sendHandoff('test-handoff-token');
  };

  const sendPaymentFailedMessage = () => {
    sendPaymentFailed('Test payment failed error');
  };

  const sendPaymentSuccessMessage = () => {
    sendPaymentSuccess();
  };

  const sendPaymentCancelledMessage = () => {
    sendPaymentCancelled();
  };

  const sendFailedMessage = () => {
    sendFailed('Test general error');
  };

  const sendRestoreFailedMessage = () => {
    sendRestoreFailed();
  };

  const sendRestoreSuccessMessage = () => {
    sendRestoreSuccess();
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>🧪 WebView Message Test</h1>
      <p>This page tests WebView messaging integration</p>

      <div style={{ marginBottom: '30px' }}>
        <h3>WebView Detection:</h3>
        <div style={{ backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '5px' }}>
          <p>
            <strong>Is WebView:</strong>
            {' '}
            {detection.isWebView ? 'Yes' : 'No'}
          </p>
          <p>
            <strong>WebView Type:</strong>
            {' '}
            {detection.webViewType}
          </p>
          <p>
            <strong>Has ReactNativeWebView:</strong>
            {' '}
            {detection.hasReactNativeWebView ? 'Yes' : 'No'}
          </p>
          <p>
            <strong>Has WebKit Handlers:</strong>
            {' '}
            {detection.hasWebkitHandlers ? 'Yes' : 'No'}
          </p>
          <p>
            <strong>Has window.parent:</strong>
            {' '}
            {detection.hasParent ? 'Yes' : 'No'}
          </p>
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h3>Test Messages:</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
          <button
            onClick={sendTestMessage}
            style={{ padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          >
            Send Test Message
          </button>

          <button
            onClick={sendHandoffMessage}
            style={{ padding: '10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          >
            Send Handoff
          </button>

          <button
            onClick={sendPaymentFailedMessage}
            style={{ padding: '10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          >
            Send Payment Failed
          </button>

          <button
            onClick={sendPaymentSuccessMessage}
            style={{ padding: '10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          >
            Send Payment Success
          </button>

          <button
            onClick={sendPaymentCancelledMessage}
            style={{ padding: '10px', backgroundColor: '#ffc107', color: 'black', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          >
            Send Payment Cancelled
          </button>

          <button
            onClick={sendFailedMessage}
            style={{ padding: '10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          >
            Send Failed
          </button>

          <button
            onClick={sendRestoreFailedMessage}
            style={{ padding: '10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          >
            Send Restore Failed
          </button>

          <button
            onClick={sendRestoreSuccessMessage}
            style={{ padding: '10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          >
            Send Restore Success
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h3>Message Types:</h3>
        <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px' }}>
          <ul>
            <li>
              <strong>handoff</strong>
              {' '}
              - Передача управления мобильному приложению
            </li>
            <li>
              <strong>pay_failed</strong>
              {' '}
              - Ошибка платежа
            </li>
            <li>
              <strong>pay_success</strong>
              {' '}
              - Успешная оплата
            </li>
            <li>
              <strong>pay_cancelled</strong>
              {' '}
              - Отмена подписки
            </li>
            <li>
              <strong>failed</strong>
              {' '}
              - Общая ошибка
            </li>
            <li>
              <strong>restore_failed</strong>
              {' '}
              - Неудачное восстановление подписки
            </li>
            <li>
              <strong>restore_success</strong>
              {' '}
              - Успешное восстановление подписки
            </li>
          </ul>
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h3>Console Logs:</h3>
        <p>Check browser console for detailed WebView message logs:</p>
        <ul>
          <li><code>WebView message sent (ReactNative):</code></li>
          <li><code>WebView message sent (Swift WebKit):</code></li>
          <li><code>WebView message sent (parent iframe):</code></li>
          <li><code>WebView message (not in WebView - browser mode):</code></li>
        </ul>
      </div>

      <div style={{ backgroundColor: '#e9ecef', padding: '15px', borderRadius: '5px' }}>
        <h4>Instructions:</h4>
        <ol>
          <li>Open this page in a WebView environment</li>
          <li>Click the test buttons to send different message types</li>
          <li>Check console logs to see which WebView method was used</li>
          <li>Verify that the mobile app receives the messages correctly</li>
        </ol>
      </div>
    </div>
  );
}
